import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Download, Share2, Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getMediaUrl } from '../utils/mediaUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResourceDetail = () => {
  const { id } = useParams();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResource();
  }, [id]);

  const fetchResource = async () => {
    try {
      const { data } = await axios.get(`${API}/resources/${id}`);
      setResource(data);
    } catch (error) {
      console.error('Failed to fetch resource:', error);
      toast.error('Resource not found');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: resource.title,
        text: resource.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-primary">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <p className="font-body text-slate-600 text-center">Loading resource...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!resource) {
    return (
      <div className="min-h-screen bg-background-primary">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24">
          <p className="font-body text-slate-600 text-center">Resource not found</p>
          <div className="text-center mt-6">
            <Link to="/" className="text-brand-accent hover:text-brand-accent-hover">
              Return to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* Back Button */}
        <Link 
          to="/library/story" 
          className="inline-flex items-center space-x-2 text-brand-accent hover:text-brand-accent-hover mb-8 font-body text-sm font-medium"
          data-testid="back-to-library-link"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to Library</span>
        </Link>

        {/* Resource Header */}
        <div className="bg-white border border-slate-200 rounded shadow-sm p-8 md:p-12 mb-8" data-testid="resource-detail-header">
          <div className="mb-6">
            <span className="inline-block bg-brand-primary/5 text-brand-primary border border-brand-primary/10 rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wider mb-4">
              {resource.category}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-brand-primary mb-4" data-testid="resource-title">
              {resource.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 font-body">
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4" />
                <span>{resource.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span>{new Date(resource.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Tags */}
          {resource.tags && resource.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Tag className="h-4 w-4 text-slate-600" />
              {resource.tags.map((tag, idx) => (
                <span key={idx} className="text-xs font-body text-slate-600 bg-slate-100 px-3 py-1 rounded">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="font-body text-base leading-relaxed text-slate-700" data-testid="resource-description">
              {resource.description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-6 border-t border-slate-200">
            {resource.pdf_url && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${resource.pdf_url}`}
                download
                className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-all duration-300"
                data-testid="download-pdf-button"
              >
                <Download className="h-4 w-4" />
                <span>Download PDF</span>
              </a>
            )}
            {resource.mp3_url && (
              <a
                href={resource.mp3_url.startsWith('http') ? resource.mp3_url : `${process.env.REACT_APP_BACKEND_URL}${resource.mp3_url}`}
                download
                className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-primary text-white font-body font-medium rounded hover:bg-brand-primary-hover transition-all duration-300"
                data-testid="download-mp3-button"
              >
                <Download className="h-4 w-4" />
                <span>Download MP3</span>
              </a>
            )}
            <button
              onClick={handleShare}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-transparent border border-slate-300 text-slate-700 font-body font-medium rounded hover:bg-slate-50 transition-all duration-300"
              data-testid="share-button"
            >
              <Share2 className="h-4 w-4" />
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Audio Player */}
        {resource.mp3_url && (
          <div className="bg-brand-primary border border-slate-200 rounded shadow-sm p-8 mb-8" data-testid="audio-player-section">
            <h3 className="font-heading text-xl font-medium text-white mb-4">Listen Now</h3>
            <audio 
              controls 
              className="w-full"
              style={{ height: '54px' }}
              preload="metadata"
              crossOrigin="anonymous"
              data-testid="audio-player"
            >
              <source src={getMediaUrl(resource.mp3_url)} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        )}

        {/* Video Player - YouTube or Direct MP4 */}
        {(resource.youtube_url || resource.video_url) && (
          <div className="bg-white border border-slate-200 rounded shadow-sm p-8 mb-8" data-testid="video-player-section">
            <h3 className="font-heading text-xl font-medium text-brand-primary mb-4">Watch Video</h3>
            <div className="aspect-video">
              {resource.youtube_url ? (
                <iframe
                  className="w-full h-full rounded"
                  src={resource.youtube_url.replace('watch?v=', 'embed/')}
                  title={resource.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="youtube-video-player"
                ></iframe>
              ) : (
                <video
                  controls
                  className="w-full h-full rounded bg-black"
                  preload="metadata"
                  playsInline
                  data-testid="direct-video-player"
                >
                  <source src={getMediaUrl(resource.video_url)} type="video/mp4" />
                  Your browser does not support the video element.
                </video>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ResourceDetail;